import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { AccessTime } from "@mui/icons-material";
import { MessageBubbleContainer, MessageContent } from "./SharedStyledComponents";

const PaymentBillSplitEvenlyMessage = ({
  message,
  isOwn,
  currentUser,
  storedUserId,
  onViewOrder,
  onOpenPaymentDialog,
  getReadReceiptStatus,
  getCurrencySymbol,
  formattedMessage,
  wrappedOnOpenPaymentDialog,
  theme,
}) => {
  const currentUserId = currentUser?.userId ?? storedUserId;
  const orderId = message?.orderId;
  
  let currentUserOrder = null;
  let amountToPay = 0;
  let remainingAmount = 0;
  let paymentStatus = null;
  let currencyCode = "USD";
  let hasAcceptedSplitEvenly = false;
  
  if (orderId && typeof orderId === "object" && orderId.groupOrder) {
    const userOrders = orderId?.groupOrder?.userOrders || [];
    currentUserOrder = userOrders.find((userOrder) => {
      const userId = userOrder.userId?._id || userOrder.userId;
      return userId === currentUserId;
    });
    
    hasAcceptedSplitEvenly = currentUserOrder?.paymentType === "split_evenly";
    
    const paymentFlow = orderId?.paymentFlow;
    const responses = paymentFlow?.request?.responses || [];
    const userResponse = responses.find((r) => {
      const userId = r.userId?._id || r.userId;
      return userId === currentUserId;
    });
    const hasApprovedResponse = userResponse?.response === "approved" || userResponse?.response === "accepted";
    
    hasAcceptedSplitEvenly = hasAcceptedSplitEvenly || hasApprovedResponse;
    
    amountToPay = currentUserOrder?.amountToPay || 0;
    remainingAmount = currentUserOrder?.remainingAmount || 0;
    paymentStatus = currentUserOrder?.paymentStatus;
    currencyCode = orderId?.currency || "USD";
  } else {
    const isUserInInitiationFor =
      message.initiationFor && message.initiationFor.includes(currentUserId);
    hasAcceptedSplitEvenly = isUserInInitiationFor;
    
    amountToPay = message?.amount || 0;
    remainingAmount = message?.amount || 0;
    paymentStatus = "pending";
    if (orderId && typeof orderId === "object") {
      currencyCode = orderId?.currency || "USD";
    }
  }

  const isUserInInitiationFor =
    message.initiationFor && message.initiationFor.includes(currentUserId);
  
  if (!hasAcceptedSplitEvenly && !isUserInInitiationFor) {
    return null;
  }

  const isPaymentComplete = 
    paymentStatus === "paid" || 
    paymentStatus === "upfront_paid";
  
  const hasAmountToPay = amountToPay > 0 || remainingAmount > 0;
  
  const shouldShowPayButton =
    hasAmountToPay &&
    !isPaymentComplete;

  const hasUserPaid =
    paymentStatus === "paid" ||
    paymentStatus === "upfront_paid";

  return (
    <MessageBubbleContainer isOwn={isOwn}>
      {!isOwn && (
        <Typography
          variant="caption"
          sx={{
            color: "#374151",
            fontSize: { xs: "0.75rem", sm: "0.8rem" },
            fontWeight: 600,
            mb: 0.5,
            alignSelf: "flex-start",
          }}
        >
          {message.senderId?.fullName || message.senderId?.userName || "user"}
        </Typography>
      )}

      <MessageContent
        isOwn={isOwn}
        isTemp={message.isTemp}
        isOrder={false}
        isPayment={true}
        isRequest={false}
        idDraft={false}
        isDisabled={false}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%",
            minWidth: "280px",
            maxWidth: "380px",
          }}
        >
          <Box
            sx={{
              color: isOwn ? "#FFFFFF" : "#000000",
              fontSize: "16px",
              fontWeight: 600,
              lineHeight: 1.3,
              textAlign: "left",
            }}
          >
            {formattedMessage.text || message.message || "Split payment approved"}
          </Box>

          {shouldShowPayButton && onViewOrder && (
            <Button
              variant="contained"
              onClick={(e) => {
                e.stopPropagation();
                wrappedOnOpenPaymentDialog(message);
              }}
              sx={{
                backgroundColor: isOwn ? "#ffffff" : "#7C3AED",
                color: isOwn ? "#7C3AED" : "#ffffff",
                borderRadius: 2,
                px: 3,
                py: 1,
                fontSize: "14px",
                fontWeight: 600,
                textTransform: "none",
                alignSelf: "flex-start",
                "&:hover": {
                  backgroundColor: isOwn ? "#f3f4f6" : "#6d28d9",
                },
              }}
            >
              {(() => {
                const amountToDisplay = 
                  amountToPay > 0 
                    ? amountToPay 
                    : (message?.amount || 0 || remainingAmount);
                const currencySymbol = getCurrencySymbol(currencyCode);
                return `pay ${currencySymbol}${amountToDisplay.toFixed(2)}`;
              })()}
            </Button>
          )}

          {hasUserPaid && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                backgroundColor: "rgba(34, 197, 94, 0.1)",
                color: "#16a34a",
                padding: theme.spacing(1, 2),
                borderRadius: theme.spacing(1),
                border: "1px solid rgba(34, 197, 94, 0.2)",
                alignSelf: "flex-start",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                }}
              >
                ✓ Payment completed
              </Typography>
            </Box>
          )}
        </Box>
      </MessageContent>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: isOwn ? "flex-end" : "flex-start",
          gap: 1,
          mt: 1,
          width: "100%",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: "rgba(0,0,0,0.6)",
            fontSize: { xs: "0.7rem", sm: "0.75rem" },
          }}
        >
          {message.timestamp}
        </Typography>

        {isOwn && (
          <Typography
            variant="caption"
            sx={{
              color: "rgba(0,0,0,0.6)",
              fontSize: { xs: "0.7rem", sm: "0.75rem" },
            }}
          >
            {message.isTemp ? (
              <AccessTime fontSize="inherit" />
            ) : (
              getReadReceiptStatus() || "✓"
            )}
          </Typography>
        )}
      </Box>
    </MessageBubbleContainer>
  );
};

export default PaymentBillSplitEvenlyMessage;
